import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Field, Input, Text, VStack, Center, Image } from '@chakra-ui/react';
import PasswordInput from '@/components/ui/foundation/PasswordInput';
import Logo from '@/assets/images/cyberjob_dialogflow_logo.svg';
import { useAuth } from '@/auth/AuthContext';
import useResendCooldown from '@/auth/useResendCooldown';
import AccountService from '@/services/AccountService';

const LoginPage = () => {
    const { t } = useTranslation('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login, completeTwoFactorLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [step, setStep] = useState('credentials');
    const [pendingToken, setPendingToken] = useState('');
    const [twoFactorMethod, setTwoFactorMethod] = useState('Totp');
    const [twoFactorInfo, setTwoFactorInfo] = useState('');
    const [isResending, setIsResending] = useState(false);
    const resendCooldown = useResendCooldown();
    const isEmailMethod = twoFactorMethod === 'Email';
    const [code, setCode] = useState('');
    const [twoFactorError, setTwoFactorError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);

    const redirectTo = location.state?.from?.pathname ?? '/models';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const result = await login(email, password);
            if (result?.success) {
                navigate(redirectTo, { replace: true });
            } else if (result?.requiresTwoFactor) {
                setPendingToken(result.pendingToken);
                setTwoFactorMethod(result.twoFactorMethod);
                if (result.twoFactorMethod === 'Email') resendCooldown.start();
                setStep('twoFactor');
            } else {
                setError(result?.error || t('loginPage.errorInvalidCredentials'));
            }
        } catch (err) {
            setError(err?.response?.status === 401
                ? t('loginPage.errorInvalidCredentials')
                : t('loginPage.errorGeneric'));
        } finally {
            setIsLoading(false);
        }
    };

    const invalidCodeMessage = isEmailMethod
        ? t('loginPage.twoFactor.errorInvalidEmailCode')
        : t('loginPage.twoFactor.errorInvalidCode');

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsVerifying(true);
        try {
            const result = await completeTwoFactorLogin(pendingToken, code);
            if (result?.success) {
                navigate(redirectTo, { replace: true });
            } else {
                setTwoFactorError(invalidCodeMessage);
            }
        } catch {
            setTwoFactorError(invalidCodeMessage);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        setTwoFactorError('');
        setTwoFactorInfo('');
        setIsResending(true);
        try {
            await AccountService.resendTwoFactorLoginCode(pendingToken);
            setTwoFactorInfo(t('loginPage.twoFactor.codeResent'));
            resendCooldown.start();
        } catch (err) {
            if (err?.response?.status === 429) {
                resendCooldown.start();
            } else {
                setTwoFactorError(t('loginPage.twoFactor.errorSessionExpired'));
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleBackToCredentials = () => {
        setStep('credentials');
        setCode('');
        setTwoFactorError('');
        setTwoFactorInfo('');
        setPendingToken('');
        setTwoFactorMethod('Totp');
        resendCooldown.reset();
    };

    if (step === 'twoFactor') {
        return (
            <Center minH="100vh" w="100vw" bg="bg.primary" px={{ base: 4, md: 0 }}>
                <Box w="full" maxW="400px" p={{ base: 6, md: 8 }} bg="bg.secondary" borderRadius={16}>
                    <VStack gap={6} as="form" onSubmit={handleVerifyCode}>
                        <Image src={Logo} w="70%" mb={2} />
                        <Text fontSize={24} color="white" fontWeight="bold">{t('loginPage.twoFactor.title')}</Text>
                        <Text color="text.grey" fontSize={14} textAlign="center">
                            {isEmailMethod ? t('loginPage.twoFactor.messageEmail') : t('loginPage.twoFactor.message')}
                        </Text>

                        <Field.Root required w="full">
                            <Field.Label color="white">{t('loginPage.twoFactor.codeLabel')} <Field.RequiredIndicator /></Field.Label>
                            <Input
                                variant="primary"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                placeholder={t('loginPage.twoFactor.codePlaceholder')}
                                value={code}
                                onChange={e => setCode(e.target.value)}
                            />
                        </Field.Root>

                        {twoFactorError && (
                            <Text color="red.400" fontSize={14} w="full">{twoFactorError}</Text>
                        )}
                        {twoFactorInfo && !twoFactorError && (
                            <Text color="green.300" fontSize={14} w="full">{twoFactorInfo}</Text>
                        )}

                        <Button variant="primary" type="submit" w="full" loading={isVerifying}>
                            {t('loginPage.twoFactor.submit')}
                        </Button>

                        {isEmailMethod && (
                            <Button
                                variant="outlined"
                                w="full"
                                type="button"
                                onClick={handleResendCode}
                                loading={isResending}
                                disabled={resendCooldown.isCoolingDown}
                            >
                                {resendCooldown.isCoolingDown
                                    ? t('loginPage.twoFactor.resendCooldown', { seconds: resendCooldown.secondsLeft })
                                    : t('loginPage.twoFactor.resendCode')}
                            </Button>
                        )}

                        <Text
                            color="text.grey"
                            fontSize={14}
                            cursor="pointer"
                            _hover={{ color: 'white' }}
                            onClick={handleBackToCredentials}
                        >
                            {t('loginPage.twoFactor.backToLogin')}
                        </Text>

                        <Link to="/2fa-recovery">
                            <Text color="text.grey" fontSize={14} _hover={{ color: 'white' }}>
                                {isEmailMethod ? t('loginPage.twoFactor.noAccessEmail') : t('loginPage.twoFactor.noAccess')}
                            </Text>
                        </Link>
                    </VStack>
                </Box>
            </Center>
        );
    }

    return (
        <Center minH="100vh" w="100vw" bg="bg.primary" px={{ base: 4, md: 0 }}>
            <Box w="full" maxW="400px" p={{ base: 6, md: 8 }} bg="bg.secondary" borderRadius={16}>
                <VStack gap={6} as="form" onSubmit={handleSubmit}>
                    <Image src={Logo} w="70%" mb={2} />
                    <Text fontSize={24} color="white" fontWeight="bold">{t('loginPage.title')}</Text>

                    <Field.Root required w="full">
                        <Field.Label color="white">{t('loginPage.emailLabel')} <Field.RequiredIndicator /></Field.Label>
                        <Input
                            variant="primary"
                            type="email"
                            placeholder={t('loginPage.emailPlaceholder')}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </Field.Root>

                    <Field.Root required w="full">
                        <Field.Label color="white">{t('loginPage.passwordLabel')} <Field.RequiredIndicator /></Field.Label>
                        <PasswordInput value={password} onChange={e => setPassword(e.target.value)} />
                    </Field.Root>

                    {error && (
                        <Text color="red.400" fontSize={14} w="full">{error}</Text>
                    )}

                    <Button variant="primary" type="submit" w="full" loading={isLoading}>
                        {t('loginPage.submit')}
                    </Button>

                    <Link to="/forgot-password">
                        <Text color="text.grey" fontSize={14} _hover={{ color: 'white' }}>
                            {t('loginPage.forgotPassword')}
                        </Text>
                    </Link>
                </VStack>
            </Box>
        </Center>
    );
};

export default LoginPage;
